import React, { useRef, useState } from 'react';

interface VideoEmbedProps {
  youtubeId?: string;
  src?: string;
  title: string;
  className?: string;
}

const VideoEmbed: React.FC<VideoEmbedProps> = ({ youtubeId, src, title, className = '' }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isMuted, setIsMuted] = useState(true);

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(videoRef.current.muted);
    }
  };

  // Self-hosted MP4
  if (src) {
    return (
      <div className={`relative w-full aspect-video rounded-2xl overflow-hidden bg-neutral-900 ${className}`}>
        <video
          ref={videoRef}
          src={src}
          aria-label={title}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          className="absolute inset-0 w-full h-full object-contain"
        />
        <button
          type="button"
          onClick={toggleMute}
          className="absolute bottom-4 right-4 z-10 flex items-center justify-center w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors"
          aria-label={isMuted ? 'Unmute video' : 'Mute video'}
        >
          {isMuted ? (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
            </svg>
          ) : (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.536 8.464a5 5 0 010 7.072M18.364 5.636a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
            </svg>
          )}
        </button>
      </div>
    );
  }

  // YouTube placeholder
  const isPlaceholder = !youtubeId || youtubeId.startsWith('PLACEHOLDER');

  if (isPlaceholder) {
    return (
      <div className={`relative w-full aspect-video rounded-2xl overflow-hidden bg-neutral-900 flex items-center justify-center ${className}`}>
        <div className="text-center px-6">
          <svg className="w-16 h-16 text-white/40 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-white/60 text-sm font-medium">{title}</p>
          <p className="text-white/40 text-xs mt-1">Video coming soon</p>
        </div>
      </div>
    );
  }

  // YouTube embed
  return (
    <div className={`relative w-full aspect-video rounded-2xl overflow-hidden ${className}`}>
      <iframe
        src={`https://www.youtube-nocookie.com/embed/${youtubeId}?rel=0&modestbranding=1`}
        title={title}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className="absolute inset-0 w-full h-full"
        loading="lazy"
      />
    </div>
  );
};

export default VideoEmbed;
